import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import PrivateRoute from './PrivateRoute';
import MainLayout from '../components/layout/MainLayout';

const Login = lazy(() => import('../pages/login/Login'));
const Dashboard = lazy(() => import('../pages/dashboard/Dashboard'));
const Clientes = lazy(() => import('../pages/clientes/Clientes'));
const Veiculos = lazy(() => import('../pages/veiculos/Veiculos'));
const Operadores = lazy(() => import('../pages/operadores/Operadores'));
const Jornadas = lazy(() => import('../pages/jornadas/Jornadas'));
const Checklists = lazy(() => import('../pages/checklists/Checklists'));
const Acionamentos = lazy(() => import('../pages/atendimentos/Atendimentos'));
const Prestadores = lazy(() => import('../pages/prestadores/Prestadores'));
const Empresas = lazy(() => import('../pages/empresas/Empresas'));
const Servicos = lazy(() => import('../pages/servicos/Servicos'));
const Planos = lazy(() => import('../pages/planos/Planos'));
const AcionamentoPublico = lazy(() => import('../pages/acionamento-publico/AcionamentoPublico'));
const PortalPrestador = lazy(() => import('../pages/prestador/PortalPrestador'));
const AtendimentosOnline = lazy(() => import('../pages/atendimentos-online/AtendimentosOnline'));
const LinksOnline = lazy(() => import('../pages/links-online/LinksOnline'));
const ApiPlaca = lazy(() => import('../pages/api-placa/ApiPlaca'));
const Configuracoes = lazy(() => import('../pages/configuracoes/Configuracoes'));
const Painel = lazy(() => import('../pages/painel/Painel'));
const Pagamentos = lazy(() => import('../pages/pagamentos/Pagamentos'));
const Faturamentos = lazy(() => import('../pages/faturamentos/Faturamentos'));
const Comunicacoes = lazy(() => import('../pages/comunicacoes/Comunicacoes'));
const GateEmail = lazy(() => import('../pages/gates/GateEmail'));
const GateSms = lazy(() => import('../pages/gates/GateSms'));
const GateWhatsapp = lazy(() => import('../pages/gates/GateWhatsapp'));
const GateAsaas = lazy(() => import('../pages/gates/GateAsaas'));
const NotasFiscais = lazy(() => import('../pages/notas-fiscais/NotasFiscais'));
const Cotacoes = lazy(() => import('../pages/cotacoes/Cotacoes'));
const SuperAdmin = lazy(() => import('../pages/super/SuperAdmin'));
const Fechamentos = lazy(() => import('../pages/fechamentos/Fechamentos'));
const Relatorios = lazy(() => import('../pages/relatorios/Relatorios'));
const Tickets = lazy(() => import('../pages/tickets/Tickets'));
const UsuariosPortal = lazy(() => import('../pages/usuarios-portal/UsuariosPortal'));
const CategoriasTickets = lazy(() => import('../pages/categorias-tickets/CategoriasTickets'));
const PortalLogin = lazy(() => import('../pages/portal/PortalLogin'));
const PortalDashboard = lazy(() => import('../pages/portal/PortalDashboard'));
const PortalNovoTicket = lazy(() => import('../pages/portal/PortalNovoTicket'));
const PortalDetalheTicket = lazy(() => import('../pages/portal/PortalDetalheTicket'));

const LoadingFallback = () => (
  <div className="loading-screen">
    <div className="spinner" />
    <p>Carregando...</p>
  </div>
);

export default function AppRoutes() {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/acionamento/:slug" element={<AcionamentoPublico />} />
        {/* Sem login: o token da URL e a credencial. */}
        <Route path="/prestador/:token" element={<PortalPrestador />} />
        <Route path="/acionamento/:slug/acompanhar/:trackingToken" element={<AcionamentoPublico />} />

        {/* Portal de Tickets - publico (sem MainLayout) */}
        {/*
          A area do dono do SaaS fica FORA do painel: outro login, outro token,
          outro segredo. Sem `PrivateRoute` porque o guarda dela e proprio — o
          `AuthContext` do painel nao sabe nada sobre este papel, e misturar os
          dois seria a maneira mais rapida de um cliente ver a lista de todos os
          outros.
        */}
        <Route path="/super" element={<SuperAdmin />} />

        <Route path="/tickets" element={<Navigate to="/tickets/login" replace />} />
        <Route path="/tickets/login" element={<PortalLogin />} />
        <Route path="/tickets/painel" element={<PortalDashboard />} />
        <Route path="/tickets/novo" element={<PortalNovoTicket />} />
        <Route path="/tickets/ver/:id" element={<PortalDetalheTicket />} />

        <Route
          path="/"
          element={
            <PrivateRoute>
              <MainLayout />
            </PrivateRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="clientes" element={<Clientes />} />
          <Route path="veiculos" element={<Veiculos />} />
          <Route path="acionamentos" element={<Acionamentos />} />
          <Route path="painel" element={<Painel />} />
          <Route path="atendimentos-online" element={<AtendimentosOnline />} />
          <Route path="links-online" element={<LinksOnline />} />
          <Route path="atendimentos" element={<Navigate to="/acionamentos" replace />} />
          <Route path="empresas" element={<Empresas />} />
          <Route path="servicos" element={<Servicos />} />
          <Route path="planos" element={<Planos />} />
          <Route path="prestadores" element={<Prestadores />} />
          <Route path="operadores" element={
            <PrivateRoute modulo="operadores"><Operadores /></PrivateRoute>
          } />
          {/* Sem PrivateRoute por módulo: a escala do próprio time é coisa que
              qualquer operador precisa ver para saber a quem passar o plantão.
              Encerrar jornada alheia é que exige nível, e isso o servidor
              confere. */}
          <Route path="jornadas" element={<Jornadas />} />
          {/* Mesmo modulo dos tipos de atendimento: quem cadastra o tipo e quem
              cadastra a lista de perguntas dele. */}
          <Route path="checklists" element={
            <PrivateRoute modulo="planos"><Checklists /></PrivateRoute>
          } />
          <Route path="pagamentos" element={<Pagamentos />} />
          <Route path="faturamentos" element={<Faturamentos />} />
          <Route path="comunicacoes" element={<Comunicacoes />} />
          <Route path="gate-email" element={<GateEmail />} />
          <Route path="gate-whatsapp" element={<GateWhatsapp />} />
          <Route path="gate-sms" element={<GateSms />} />
          <Route path="gate-asaas" element={<GateAsaas />} />
          <Route path="notas-fiscais" element={<NotasFiscais />} />
          <Route path="cotacoes" element={<Cotacoes />} />
          <Route path="fechamentos" element={<Fechamentos />} />
          <Route path="relatorios" element={<Relatorios />} />
          <Route path="api-placa" element={<ApiPlaca />} />
          <Route path="tickets-admin" element={<Tickets />} />
          <Route path="usuarios-portal" element={<UsuariosPortal />} />
          <Route path="categorias-tickets" element={<CategoriasTickets />} />
          {/* Sem `modulo`: preferencia pessoal de quem esta logado, nao
              configuracao da central. Ver o comentario no Sidebar. */}
          <Route path="configuracoes" element={<Configuracoes />} />
        </Route>

        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Suspense>
  );
}
